import debounce from "lodash.debounce";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";
import { v4 as createUuid } from "uuid";

import type { TopicResult } from "api/api.types";
import { dimensionsApiSlice } from "api/dimensionApi.slice";
import { useLazyGetSimilarTopicsByDimensionQuery } from "api/topicsApi.slice";
import { ErrorMessage, SectionTitleUnderlined, SelectInput, UIButton } from "common/components";
import { ClearInput } from "common/components/form/ClearInput";
import type { SelectInputOption } from "common/components/form/SelectInput";
import { TextArea } from "common/components/form/TextArea";
import { coalesceArray, useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { addNewCustomKeyIssueToMap, setNewCustomDimensionTopics } from "domains/createMapDetail/actions";
import { getCurrentMap, getCurrentMapIsNew } from "domains/createMapDetail/selectors";
import {
  createCustomKeyIssue,
  resetTopicSearchIdsForQuery,
  searchTopicForCustomKeyIssue,
} from "domains/customKeyIssue/actions";
import { StyledSubtitle } from "domains/customKeyIssue/components/shared";
import { SimilarKnowledge } from "domains/customKeyIssue/components/SimilarKnowledge";
import {
  getCustomDimensionTopicListIsPending,
  getCustomKeyIssueIsUploading,
  getCustomKeyIssueTopicList,
  getCustomKeyIssueUploadHasErrored,
} from "domains/customKeyIssue/selectors";
import { useUploadDimension } from "domains/customKeyIssue/useUploadDimension";
import { dimensionSlice } from "domains/dimension/dimension.slice";
import type { NewCustomDimension } from "domains/dimension/dimension.types";
import { createTopicsSelector } from "entities/topics/selectors";
import type { Topic } from "entities/topics/types";

type DimensionTextData = { description: string; name: string; parent_topic_name: string; subtitle: string };

export const AddCustomKeyIssue = () => {
  const dispatch = useDispatch();
  const [fetchSimilarTopicsByDimension, similarTopicsResult] = useLazyGetSimilarTopicsByDimensionQuery();
  const topics = useMemoisedSelector(getCustomKeyIssueTopicList, createTopicsSelector, coalesceArray);
  const currentMap = useSelector(getCurrentMap);
  const isNewCustomMap = useSelector(getCurrentMapIsNew);
  const isTopicsPending = useSelector(getCustomDimensionTopicListIsPending);
  const isUploading = useSelector(getCustomKeyIssueIsUploading);
  const hasUploadFailed = useSelector(getCustomKeyIssueUploadHasErrored);
  const [selectedTopics, setSelectedTopics] = useState<(Topic | TopicResult)[]>([]);
  const translate = useTranslate();
  const { dimension, isFetching } = useUploadDimension();

  const [description, setDescription] = useState(dimension?.description ?? "");
  const [subtitle, setSubtitle] = useState(dimension?.subtitle ?? "");
  const [title, setTitle] = useState(dimension?.name ?? "");

  const isCreateMode = dimension === undefined;

  useEffect(() => {
    if (!isCreateMode) setSelectedTopics(dimension.topics);
  }, [dimension?.topics, isCreateMode]);

  useEffect(() => {
    dispatch(resetTopicSearchIdsForQuery());
  }, [dispatch]);

  useEffect(() => {
    if (isCreateMode) return;
    void fetchSimilarTopics({ description, name: title, parent_topic_name: currentMap?.name ?? "", subtitle });
    setSelectedTopics(dimension.topics);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode]);

  const fetchSimilarTopics = (data: DimensionTextData) =>
    fetchSimilarTopicsByDimension(
      { body: { data, ignore_ids: currentMap?.id === undefined ? null : [currentMap.id] }, search: { limit: 5 } },
      true,
    );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSimilarTopics = useCallback(
    debounce(fetchSimilarTopics, 500, { leading: true, trailing: true }),
    [],
  );

  const onChangeTitle = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setTitle(value);
    void debouncedFetchSimilarTopics({ description, name: value, parent_topic_name: currentMap?.name ?? "", subtitle });
  };

  const onChangeSubtitle = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setSubtitle(value);
    void debouncedFetchSimilarTopics({
      description,
      name: title,
      parent_topic_name: currentMap?.name ?? "",
      subtitle: value,
    });
  };

  const onChangeDescription = (value: string) => {
    setDescription(value);
    void debouncedFetchSimilarTopics({
      description: value,
      name: title,
      parent_topic_name: currentMap?.name ?? "",
      subtitle,
    });
  };

  const onAddTopicSuggestion = (item: SelectInputOption) => {
    const topic = similarTopicsResult.data?.find(({ id }) => id === item.value);
    if (topic === undefined) return;
    const topics = [...selectedTopics.filter(({ id }) => id !== item.value), topic];
    setSelectedTopics(topics);
    dispatch(setNewCustomDimensionTopics({ id: item.value, topics }));
  };

  const onClearTitle = () => {
    setTitle("");
    void fetchSimilarTopics({ description, name: "", parent_topic_name: currentMap?.name ?? "", subtitle });
  };

  const onClearSubtitle = () => {
    setSubtitle("");
    void fetchSimilarTopics({ description, name: title, parent_topic_name: currentMap?.name ?? "", subtitle: "" });
  };

  const onMapSelected = (item: SelectInputOption) => {
    const topic = topics.find(({ id }) => id === item.value);
    if (topic !== undefined) setSelectedTopics([...selectedTopics, topic]);
  };

  const onTopicsRemoved = (removedItem: SelectInputOption) =>
    setSelectedTopics(selectedTopics.filter(({ id }) => id !== removedItem.value));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchTopicsHandle = useCallback(
    debounce((query: string) => {
      if (query.length > 0) dispatch(searchTopicForCustomKeyIssue({ context: currentMap?.id, query }));
    }, 500),
    [dispatch],
  );

  const onSubmit = () => {
    const body: NewCustomDimension = {
      description,
      id: dimension?.id ?? createUuid(),
      image_url: dimension?.image_url ?? null,
      is_custom_dimension: true,
      name: title,
      parent_topic_id: currentMap?.id,
      status: "Active",
      subtitle,
      topics: selectedTopics,
    };

    if (isNewCustomMap) {
      dispatch(addNewCustomKeyIssueToMap(body));
      return;
    }

    const dimensionCacheUpdateAction = dimensionsApiSlice.util.updateQueryData(
      "getDimension",
      { id: body.id },
      (draft) => {
        draft.dimension.description = body.description;
        draft.dimension.name = body.name;
        draft.dimension.subtitle = body.subtitle;
        draft.dimension.topic_ids = body.topics.map((topic) => topic.id);
        draft.topics = body.topics as TopicResult[];
      },
    );
    dispatch(dimensionCacheUpdateAction);
    dispatch(dimensionSlice.actions.sendRefetchSignal());
    dispatch(createCustomKeyIssue(body));
  };

  const validateTitleCharLength = title.length <= TITLE_CHAR_LIMIT;
  const isValid = !!title && validateTitleCharLength && selectedTopics.length !== 0;
  const isFieldsDisabled = isFetching || isUploading;

  const term = isNewCustomMap
    ? isCreateMode
      ? "app.create.create_map.add"
      : "app.create.create_map.update"
    : isCreateMode
      ? "app.create.create_map.add"
      : "app.create.create_map.save";

  const dropdownOptions = topics.map((topic) => ({
    description: topic.description,
    images: topic.images,
    label: topic.name,
    value: topic.id,
  }));

  const suggestionOptions = similarTopicsResult.isFetching
    ? []
    : (similarTopicsResult.data
        ?.filter(({ id }) => !selectedTopics.some((topic) => topic.id === id))
        .map(({ id, name }) => ({ label: name, value: id })) ?? []);

  return (
    <>
      <StyledCharLimitLabel valid={validateTitleCharLength}>
        {title.length}/{TITLE_CHAR_LIMIT}
      </StyledCharLimitLabel>
      <StyledInput
        autoComplete="off"
        dataTestId="add-custom-key-issue-title-input"
        disabled={isFieldsDisabled}
        label={translate("app.custom_key_issue.title")}
        name="key-issue-title"
        onChange={onChangeTitle}
        onClearHandle={onClearTitle}
        value={title}
      />
      <StyledInput
        dataTestId="add-custom-key-issue-subtitle-input"
        disabled={isFieldsDisabled}
        label={translate("app.custom_key_issue.subtitle")}
        name="subtitle"
        onChange={onChangeSubtitle}
        onClearHandle={onClearSubtitle}
        value={subtitle}
      />
      <TextArea
        dataTestId="add-custom-key-issue-description-textarea"
        disabled={isFieldsDisabled}
        onValueChange={onChangeDescription}
        placeholder={translate("app.custom_key_issue.description")}
        value={description}
      />
      <StyledSectionTitleUnderlined>{translate("app.custom_key_issue.connect_to_maps")}</StyledSectionTitleUnderlined>
      <StyledSubtitle>{translate("app.custom_key_issue.search_topics.help_text")}</StyledSubtitle>
      <SelectInput
        dataTestId="add-custom-key-issue-connected-map-selector"
        defaultItems={selectedTopics.map((topic) => ({ label: topic.name, value: topic.id }))}
        disabled={isFieldsDisabled}
        dropdownOptions={dropdownOptions}
        isPending={isTopicsPending}
        label={translate("app.search.placeholder")}
        onAddSuggestion={onAddTopicSuggestion}
        onInputChange={searchTopicsHandle}
        onItemRemove={onTopicsRemoved}
        onItemSelect={onMapSelected}
        placeholder={translate("app.custom_key_issue.search_topics.explainer")}
        suggestionOptions={suggestionOptions}
      />
      <SimilarKnowledge description={description} name={title} subtitle={subtitle} />
      <StyledErrorMessage isActive={hasUploadFailed}>{translate("app.auth.error")}</StyledErrorMessage>
      <UIButton
        data-test-id="submit-new-custom-key-issue-button"
        disabled={!isValid || isFetching}
        fullwidth
        isLoading={isUploading}
        onClick={onSubmit}
        style={{ margin: "1rem 0" }}
      >
        {translate(term)}
      </UIButton>
    </>
  );
};

const TITLE_CHAR_LIMIT = 60;

const StyledCharLimitLabel = styled.p<{ valid: boolean }>`
  display: flex;
  justify-content: flex-end;
  font-size: 0.8rem;
  color: ${({ theme, valid }) => (valid ? theme.colors.textFade : theme.colors.red)};
`;

const StyledInput = styled(ClearInput)`
  margin-bottom: 1rem;
`;

const StyledSectionTitleUnderlined = styled(SectionTitleUnderlined)`
  margin-top: 2rem;
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 0;
`;
